import { EngineHost } from '../engine/EngineHost';
import type { AudioSystem } from '../engine/AudioSystem';
import { MAX_BANKS, Meter } from '../engine/protocol';
import type { RecordSource, SliceMode } from '../engine/protocol';
import { mapProgram } from '../programs/mapper';
import type { MappedProgram } from '../programs/mapper';
import type { ProgramData } from '../programs/schema';

/** What a deck needs from its session (structural — avoids a cyclic import). */
export interface SessionLike {
  readonly system: AudioSystem;
  readonly bpm: number;
  readonly tempoSet: boolean;
  setBpm(bpm: number): void;
  adoptTempo(bpm: number): void;
  tapTempo(): void;
  connectInput(): Promise<void>;
}

export type StoreEvent = 'program' | 'transport' | 'hold' | 'input' | 'banks' | 'boot';

export const REC_LENGTHS = [1, 2, 4, 8, 16, 32] as const;

export interface BankState {
  slices: 8 | 16;
  mask: number;
  mode: SliceMode;
  loop: boolean;
}

/** One captured pad state, timestamped in beats from motion start. */
export interface MotionFrame {
  beat: number;
  t1: boolean;
  x1: number;
  y1: number;
  t2: boolean;
  x2: number;
  y2: number;
}

export interface Motion {
  frames: MotionFrame[];
  lengthBeats: number;
}

/** A program memory slot (hardware has 8): program + performance state. */
export interface MemorySlot {
  programId: string;
  fxDepth: number;
  motion: Motion | null;
}

const MEMORY_KEY = 'kpv-memories-v1';

/**
 * One KPV deck's state. UI reads from here and calls the intent methods;
 * the store forwards continuous control to its engine. Tempo and audio
 * input are session-wide — those calls delegate to the Session.
 */
export class KpvStore {
  readonly engine: EngineHost;

  current: MappedProgram | null = null;
  fxDepth = 1;
  hold = false;

  /** Second-touch OFF by default — stray fingers/palms can't fire sub-FX.
   *  MIDI-driven touch 2 (hardware controllers) is always honored. */
  twoFingerMode = false;

  /** Sampling-mode state: arm → pick length/source → press a bank to record. */
  samplingMode = false;
  recBeats: (typeof REC_LENGTHS)[number] = 4;
  recSource: RecordSource = 'input';
  /** How many banks the UI shows (grow with addBank, up to MAX_BANKS). */
  visibleBanks = 4;
  /** Which bank the slice editor targets. */
  selectedBank = 0;
  bankStates: BankState[] = Array.from({ length: MAX_BANKS }, () => ({ slices: 8, mask: 0xffff, mode: 'skip' as SliceMode, loop: true }));
  overviews: Array<Float32Array | null> = Array.from({ length: MAX_BANKS }, () => null);

  mute = false;
  fxRelease = 0.35;
  /** How loud you hear the live input (recording always captures full level). */
  monitorLevel = 1;
  monitorOn = true;

  memories: Array<MemorySlot | null> = Array.from({ length: 8 }, () => null);

  /** Pad motion recorder. */
  motion: Motion | null = null;
  motionRecording = false;
  motionPlaying = false;
  private motionStartBeat = 0;
  private motionCursor = 0;

  /** Physical pointer state, pre-hold. */
  private touch1Down = false;
  private touch2Down = false;
  private last1: [number, number] = [0.5, 0.5];
  private last2: [number, number] = [0.5, 0.5];

  private listeners = new Map<StoreEvent, Set<() => void>>();

  constructor(
    private readonly session: SessionLike,
    public readonly data: ProgramData
  ) {
    this.engine = new EngineHost(session.system);
  }

  on(ev: StoreEvent, fn: () => void): () => void {
    if (!this.listeners.has(ev)) this.listeners.set(ev, new Set());
    this.listeners.get(ev)!.add(fn);
    return () => this.listeners.get(ev)!.delete(fn);
  }

  private emit(ev: StoreEvent): void {
    this.listeners.get(ev)?.forEach((fn) => fn());
  }

  private tempoDefiningRec: { bank: number; beats: number } | null = null;

  init(): void {
    this.engine.createNode();
    this.engine.setFxDepth(this.fxDepth);
    this.engine.on((e) => {
      if (e.type === 'sampleRecorded') {
        const tdr = this.tempoDefiningRec;
        if (tdr && tdr.bank === e.bank) {
          this.tempoDefiningRec = null;
          if (e.frames > 0 && !this.session.tempoSet) {
            const bpm = (tdr.beats * 60 * this.engine.sampleRate) / e.frames;
            this.session.adoptTempo(bpm);
          }
        }
        this.emit('banks');
      }
      if (e.type === 'sampleOverview') {
        this.overviews[e.bank] = e.peaks;
        this.emit('banks');
      }
    });
    this.loadMemories();
    this.engine.setFxRelease(this.fxRelease);
    this.emit('boot');
  }

  // Session-wide conveniences (also keep MidiManager's surface stable).
  get bpm(): number {
    return this.session.bpm;
  }

  get inputLabel(): string | null {
    return this.session.system.inputLabel;
  }

  // --- Programs -----------------------------------------------------------

  selectProgram(id: string): void {
    const entry = this.data.programs.find((p) => p.id === id);
    if (!entry) return;
    const mapped = mapProgram(entry);
    this.current = mapped;
    this.engine.loadProgram(mapped.config);
    this.emit('program');
  }

  selectRelative(delta: number): void {
    if (!this.data || !this.current) return;
    const list = this.data.programs;
    const i = list.findIndex((p) => p.id === this.current!.entry.id);
    const next = list[(i + delta + list.length) % list.length];
    if (next) this.selectProgram(next.id);
  }

  // --- Pad ------------------------------------------------------------------

  pad1(down: boolean, x: number, y: number): void {
    this.touch1Down = down;
    if (down) this.last1 = [x, y];
    // While HOLD is on, releases don't reach the engine.
    if (down || !this.hold) this.engine.setTouch1(down, x, y);
  }

  pad2(down: boolean, x: number, y: number): void {
    this.touch2Down = down;
    if (down) this.last2 = [x, y];
    if (down || !this.hold) this.engine.setTouch2(down, x, y);
  }

  setTwoFingerMode(on: boolean): void {
    this.twoFingerMode = on;
    if (!on) this.engine.setTouch2(false, this.last2[0], this.last2[1]);
    this.emit('hold');
  }

  setHold(on: boolean): void {
    this.hold = on;
    this.engine.setHold(on);
    if (on) {
      // Latch current (or last) touch coordinates.
      this.engine.setTouch1(true, this.last1[0], this.last1[1]);
      if (this.touch2Down) this.engine.setTouch2(true, this.last2[0], this.last2[1]);
    } else {
      if (!this.touch1Down) this.engine.setTouch1(false, this.last1[0], this.last1[1]);
      if (!this.touch2Down) this.engine.setTouch2(false, this.last2[0], this.last2[1]);
    }
    this.emit('hold');
  }

  // --- Transport (session-wide; delegated) --------------------------------------

  setBpm(bpm: number): void {
    this.session.setBpm(bpm);
    this.emit('transport');
  }

  tapTempo(): void {
    this.session.tapTempo();
    this.emit('transport');
  }

  setFxDepth(v: number): void {
    this.fxDepth = Math.min(1, Math.max(0, v));
    this.engine.setFxDepth(this.fxDepth);
    this.emit('transport');
  }

  setMute(on: boolean): void {
    this.mute = on;
    this.engine.setMute(on);
    this.emit('transport');
  }

  setFxRelease(v: number): void {
    this.fxRelease = Math.min(1, Math.max(0, v));
    this.engine.setFxRelease(this.fxRelease);
    this.emit('transport');
  }

  setMonitorLevel(v: number): void {
    this.monitorLevel = Math.min(2, Math.max(0, v));
    if (this.monitorOn) this.engine.setMonitor(this.monitorLevel);
    this.emit('transport');
  }

  toggleMonitor(): void {
    this.monitorOn = !this.monitorOn;
    this.engine.setMonitor(this.monitorOn ? this.monitorLevel : 0);
    this.emit('transport');
  }

  // --- Program memories (M1–M8, persisted) ----------------------------------

  saveMemory(slot: number): void {
    if (!this.current) return;
    this.memories[slot] = {
      programId: this.current.entry.id,
      fxDepth: this.fxDepth,
      motion: this.motion,
    };
    this.persistMemories();
    this.emit('program');
  }

  recallMemory(slot: number): void {
    const m = this.memories[slot];
    if (!m) return;
    this.selectProgram(m.programId);
    this.setFxDepth(m.fxDepth);
    this.motion = m.motion;
    if (this.motion) this.startMotionPlayback();
    else this.stopMotionPlayback();
  }

  clearMemory(slot: number): void {
    this.memories[slot] = null;
    this.persistMemories();
    this.emit('program');
  }

  private persistMemories(): void {
    try {
      localStorage.setItem(MEMORY_KEY, JSON.stringify(this.memories));
    } catch {
      /* storage may be unavailable; memories stay session-only */
    }
  }

  private loadMemories(): void {
    try {
      const raw = localStorage.getItem(MEMORY_KEY);
      if (raw) this.memories = JSON.parse(raw) as Array<MemorySlot | null>;
    } catch {
      /* corrupt storage: start fresh */
    }
  }

  // --- Pad motion recorder ----------------------------------------------------

  get currentBeat(): number {
    return this.engine.meters[Meter.Beat] ?? 0;
  }

  toggleMotionRecord(): void {
    if (this.motionRecording) {
      // stop: quantize length up to a whole beat (min 1)
      const len = Math.max(1, Math.ceil(this.currentBeat - this.motionStartBeat));
      if (this.motion && this.motion.frames.length > 1) {
        this.motion.lengthBeats = len;
        this.motionRecording = false;
        this.startMotionPlayback();
      } else {
        this.motion = null;
        this.motionRecording = false;
        this.motionPlaying = false;
      }
    } else {
      this.motion = { frames: [], lengthBeats: 0 };
      this.motionRecording = true;
      this.motionPlaying = false;
      this.motionStartBeat = this.currentBeat;
    }
    this.emit('transport');
  }

  startMotionPlayback(): void {
    if (!this.motion || this.motion.frames.length < 2) return;
    this.motionPlaying = true;
    this.motionStartBeat = this.currentBeat;
    this.motionCursor = 0;
    this.emit('transport');
  }

  stopMotionPlayback(): void {
    if (!this.motionPlaying) return;
    this.motionPlaying = false;
    this.engine.setTouch1(false, this.last1[0], this.last1[1]);
    this.engine.setTouch2(false, this.last2[0], this.last2[1]);
    this.emit('transport');
  }

  /** Called every UI frame. Records or replays pad motion, beat-synced. */
  motionTick(liveTouching: boolean): void {
    if (this.motionRecording && this.motion) {
      const c = this.engine.ctrl;
      this.motion.frames.push({
        beat: this.currentBeat - this.motionStartBeat,
        t1: c[0]! > 0.5,
        x1: c[1]!,
        y1: c[2]!,
        t2: c[3]! > 0.5,
        x2: c[4]!,
        y2: c[5]!,
      });
      return;
    }
    if (!this.motionPlaying || !this.motion || liveTouching) return; // live hands win
    const m = this.motion;
    const phase = (this.currentBeat - this.motionStartBeat) % m.lengthBeats;
    // advance cursor (frames are time-ordered; wrap resets it)
    if (this.motionCursor >= m.frames.length || m.frames[this.motionCursor]!.beat > phase) {
      this.motionCursor = 0;
    }
    while (
      this.motionCursor < m.frames.length - 1 &&
      m.frames[this.motionCursor + 1]!.beat <= phase
    ) {
      this.motionCursor++;
    }
    const f = m.frames[this.motionCursor]!;
    this.engine.setTouch1(f.t1, f.x1, f.y1);
    this.engine.setTouch2(f.t2, f.x2, f.y2);
  }

  // --- Sampler -------------------------------------------------------------------

  bankState(bank: number): number {
    return this.engine.meters[Meter.SampleBankState + bank] ?? 0;
  }

  bankPos(bank: number): number {
    return this.engine.meters[Meter.SamplePos + bank] ?? 0;
  }

  bankPress(bank: number): void {
    this.selectedBank = bank;
    const state = this.bankState(bank);
    if (state === 3) {
      // recording → stop early
      this.engine.sampleRecord(bank, false);
    } else if (this.samplingMode) {
      this.bankStates[bank]!.mask = 0xffff;
      // No tempo claimed yet? Record free-length — this take DEFINES the
      // tempo: LEN declares how many beats it counts as; stop with a press.
      const freeLength = !this.session.tempoSet;
      if (freeLength) this.tempoDefiningRec = { bank, beats: this.recBeats };
      this.engine.sampleRecord(bank, true, this.recBeats, this.recSource, freeLength);
      this.samplingMode = false;
    } else {
      this.engine.sampleTrigger(bank, state !== 2);
    }
    this.emit('banks');
  }

  setSamplingMode(on: boolean): void {
    this.samplingMode = on;
    this.emit('banks');
  }

  setRecBeats(beats: (typeof REC_LENGTHS)[number]): void {
    this.recBeats = beats;
    this.emit('banks');
  }

  setRecSource(source: RecordSource): void {
    this.recSource = source;
    this.emit('banks');
  }

  addBank(): void {
    if (this.visibleBanks < MAX_BANKS) {
      this.visibleBanks++;
      this.emit('banks');
    }
  }

  selectBank(bank: number): void {
    this.selectedBank = bank;
    this.emit('banks');
  }

  private pushSlices(bank: number): void {
    const bs = this.bankStates[bank]!;
    this.engine.sampleSlices(bank, bs.slices, bs.mask, bs.mode);
    this.emit('banks');
  }

  toggleSlice(bank: number, slice: number): void {
    const bs = this.bankStates[bank];
    if (!bs) return;
    bs.mask ^= 1 << slice;
    this.pushSlices(bank);
  }

  setSliceCount(bank: number, slices: 8 | 16): void {
    const bs = this.bankStates[bank];
    if (!bs) return;
    bs.slices = slices;
    bs.mask = 0xffff; // reset chops when re-gridding
    this.pushSlices(bank);
  }

  setSliceMode(bank: number, mode: SliceMode): void {
    const bs = this.bankStates[bank];
    if (!bs) return;
    bs.mode = mode;
    this.pushSlices(bank);
  }

  setLoopMode(bank: number, loop: boolean): void {
    const bs = this.bankStates[bank];
    if (!bs) return;
    bs.loop = loop;
    this.engine.sampleLoopMode(bank, loop);
    this.emit('banks');
  }

  clearBank(bank: number): void {
    this.engine.sampleClear(bank);
    const bs = this.bankStates[bank]!;
    bs.mask = 0xffff;
    this.emit('banks');
  }

  /** Session-wide input (shared by all decks) — delegated. */
  async connectInput(): Promise<void> {
    await this.session.connectInput();
  }
}
